import { distance as turfDistance } from '@turf/turf';

/**
 * One turn on a snapped segment, positioned by metres from that segment's start.
 *
 * This is the stored shape, shared with iOS and Android — the recording apps read these
 * straight out of the hike and guide from them with no network and no map-matching.
 *
 * Distance rather than GraphHopper's `interval` index, because an index does not survive
 * the planner: bridging prepends a point, marker insertion splits a segment, reversal flips
 * it. A distance survives all three, and the recording needs a distance anyway.
 */
export interface TurnInstruction {
  d: number;
  sign: number;
  text: string;
  street?: string;
  /**
   * Set only on a cue the planner added by hand: "turn" for one with a direction, "note" for
   * one without.
   *
   * It exists because the recording apps discard GraphHopper's `text` for every sign they can
   * word themselves, and recompose the sentence from the sign and the street. That is right
   * for a routed turn and wrong for a cue, whose whole point is the words the planner typed.
   * The key is what tells them apart.
   */
  cue?: CueKind;
  /**
   * What a person wrote for this turn, if anyone did — for a cue they added and for a routed
   * turn they re-worded alike. GraphHopper's own `text` is never overwritten, so clearing this
   * key restores the original sentence for free, whatever its sign.
   */
  utext?: string;
  /**
   * The direction a person chose, overriding the one GraphHopper reported. Separate from
   * `utext` because reversing keeps a mirrored sign and drops re-worded text.
   */
  usign?: number;
  /**
   * Suppressed: kept in storage so the route still knows the turn is there, but not shown,
   * not spoken and not counted down to. A routed turn is flagged rather than removed, because
   * the next re-snap would hand it straight back.
   */
  hidden?: boolean;
}

/** Which kind of hand-added cue an instruction is, if it is one at all. */
export type CueKind = 'turn' | 'note';

/** What GraphHopper returns for a turn, of which we keep four fields. */
export interface GraphhopperInstruction {
  text?: string;
  sign?: number;
  interval?: number[];
  street_name?: string;
}

/**
 * Distance along a segment at each of its coordinates.
 *
 * The one distance convention the planner uses: this is what goes into the geometry's M
 * value at save time *and* what positions the turns, so a turn and the geometry it belongs
 * to can never end up on different scales. The recording apps correct for that disagreement
 * when they find it; producing it in the first place is worse than correcting it.
 */
export function cumulativeDistancesMeters(coordinates: number[][]): number[] {
  if (!coordinates || coordinates.length === 0) return [];

  const distances: number[] = [0];

  for (let i = 1; i < coordinates.length; i++) {
    const previous = coordinates[i - 1];
    const current = coordinates[i];
    const segmentDistance = turfDistance(
      [previous[0], previous[1]],
      [current[0], current[1]],
      { units: 'meters' }
    );

    distances.push(Math.round((distances[i - 1] + segmentDistance) * 10) / 10);
  }

  return distances;
}

/**
 * The turns GraphHopper reported, positioned along the geometry it drew them for.
 *
 * `interval[0]` is where the maneuver happens — the first point of its interval, not the
 * last — and it indexes the coordinates exactly as GraphHopper returned them, so this must
 * be measured before anything de-duplicates or trims them. Duplicate points contribute no
 * length, so the distances still line up with the de-duplicated geometry that gets saved.
 *
 * Anything unreadable is dropped rather than guessed at: a missing turn is silence, a
 * mis-placed one is a confident wrong instruction.
 */
export function turnInstructionsFrom(
  instructions: GraphhopperInstruction[] | undefined,
  rawCoordinates: number[][]
): TurnInstruction[] {
  if (!instructions || instructions.length === 0) return [];
  if (!rawCoordinates || rawCoordinates.length < 2) return [];

  const distances = cumulativeDistancesMeters(rawCoordinates);

  return instructions.reduce<TurnInstruction[]>((kept, instruction) => {
    const index = instruction.interval?.[0];
    if (index === undefined || index < 0 || index >= distances.length) return kept;

    const street = instruction.street_name?.trim();
    const turn: TurnInstruction = {
      d: distances[index],
      sign: instruction.sign ?? 0,
      text: instruction.text ?? ''
    };
    if (street) turn.street = street;

    kept.push(turn);
    return kept;
  }, []);
}
